import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { IconBaseProps, IconType } from 'react-icons';
import { FiPhoneCall, FiMail, FiMapPin, FiShoppingCart, FiUser, FiFileText } from 'react-icons/fi';
import Logo from './Logo';
import HeaderSearch from './HeaderSearch';
import { logout } from '../../../store/auth';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import type { RootState } from '../../../store';
import { useQuote } from '../../../contexts/QuoteContext';

const renderIcon = (Icon: IconType, props?: IconBaseProps) =>
  React.createElement(Icon as unknown as React.FC<IconBaseProps>, props);

const TopBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = React.useState(false);
  const cartItems = useAppSelector((state: RootState) => state.cart.items);
  const cartItemCount = React.useMemo(
    () => cartItems.reduce((total: number, item: any) => total + item.quantity, 0),
    [cartItems],
  );
  const cartTotal = React.useMemo(
    () => cartItems.reduce((total: number, item: any) => total + Number(item.price || 0) * item.quantity, 0),
    [cartItems],
  );
  const { isAuthenticated, user } = useAppSelector((state: RootState) => state.auth);
  const { itemCount: quoteItemCount } = useQuote();

  React.useEffect(() => {
    if (!isAuthenticated) {
      setIsAccountMenuOpen(false);
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (!isAccountMenuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAccountMenuOpen]);

  const handleLogout = () => {
    dispatch(logout());
    setIsAccountMenuOpen(false);
    navigate('/');
  };

  return (
    <div className="border-b border-white/10 bg-[#111111]">
      <div className="mx-auto w-full max-w-[1800px] px-3 py-2.5 min-[375px]:px-4 sm:px-6 sm:py-3 lg:px-8">
        <div className="flex min-w-0 items-center gap-1.5 min-[375px]:gap-2 sm:gap-3 lg:gap-4">
          <div className="shrink-0">
            <Logo />
          </div>

          <div className="hidden min-w-[280px] flex-1 lg:block lg:min-w-[340px] xl:min-w-[400px]">
            <HeaderSearch />
          </div>

          <div className="hidden shrink-0 items-center gap-4 min-[1700px]:flex min-[1800px]:gap-6">
            <a
              href="tel:+94773421388"
              className="group flex items-center gap-2.5 text-slate-300 transition-colors hover:text-indigo-400"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-400 transition-all group-hover:bg-indigo-500/20">
                {renderIcon(FiPhoneCall, { size: 14 })}
              </span>
              <div className="flex flex-col leading-tight">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Call Us</span>
                <span className="text-sm font-bold text-white">(+94) 77 3421388</span>
              </div>
            </a>

            <div className="h-8 w-px bg-white/10" />

            <a
              href="mailto:info@nexlanka.com"
              className="group flex items-center gap-2.5 text-slate-300 transition-colors hover:text-indigo-400"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-400 transition-all group-hover:bg-indigo-500/20">
                {renderIcon(FiMail, { size: 14 })}
              </span>
              <div className="flex flex-col leading-tight">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Email Us</span>
                <span className="text-sm font-bold text-white">info@nexlanka.com</span>
              </div>
            </a>
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-1.5 min-[375px]:gap-2 sm:gap-3">
            <Link
              className="hidden h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-300 transition-all hover:border-indigo-500/40 hover:bg-indigo-500/10 hover:text-indigo-400 min-[375px]:flex sm:h-10 sm:w-10 lg:hidden"
              to="/stores"
              title="Store Locator"
              aria-label="Store Locator"
            >
              {renderIcon(FiMapPin, { size: 18 })}
            </Link>

            <Link
              className="hidden items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-slate-300 transition-all hover:border-indigo-500/40 hover:text-indigo-400 lg:flex min-[1700px]:hidden"
              to="/stores"
            >
              {renderIcon(FiMapPin, { size: 13 })}
              Store Locator
            </Link>

            <div className="hidden h-6 w-px bg-white/10 lg:block" />

            {isAuthenticated ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  title="My Account"
                  aria-haspopup="menu"
                  aria-expanded={isAccountMenuOpen}
                  onClick={() => setIsAccountMenuOpen((current: boolean) => !current)}
                  className="group flex h-9 items-center justify-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2 text-slate-300 transition-all hover:border-indigo-500/40 hover:bg-indigo-500/10 hover:text-indigo-400 sm:h-10 sm:px-3"
                >
                  {renderIcon(FiUser, { size: 18 })}
                  <span className="hidden max-w-[80px] truncate text-xs font-semibold sm:block">
                    {user?.displayName || 'My Account'}
                  </span>
                </button>

                {isAccountMenuOpen && (
                  <div className="absolute right-0 z-30 mt-2 w-44 overflow-hidden rounded-xl border border-white/20 bg-slate-900/75 p-1.5 shadow-[0_20px_40px_rgba(15,23,42,0.65)] backdrop-blur-xl">
                    <Link
                      to="/my-account"
                      onClick={() => setIsAccountMenuOpen(false)}
                      className="block rounded-lg px-3 py-2 text-sm font-medium text-slate-200 transition hover:bg-white/10 hover:text-indigo-300"
                    >
                      My Account
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-rose-200 transition hover:bg-rose-500/15 hover:text-rose-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                title="Login"
                className="group flex h-9 items-center justify-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2 text-slate-300 transition-all hover:border-indigo-500/40 hover:bg-indigo-500/10 hover:text-indigo-400 sm:h-10 sm:px-3"
              >
                {renderIcon(FiUser, { size: 18 })}
                <span className="hidden text-xs font-semibold sm:block">Login</span>
              </Link>
            )}

            <Link
              to="/quotation"
              title="Quote List"
              className="group relative flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-300 transition-all hover:border-purple-500/40 hover:bg-purple-500/10 hover:text-purple-300 sm:h-10 sm:w-10"
            >
              {renderIcon(FiFileText, { size: 18 })}
              {quoteItemCount > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-purple-500 px-1 text-[10px] font-black leading-none text-white shadow-lg shadow-purple-900/50 transition-transform group-hover:scale-110">
                  {quoteItemCount}
                </span>
              )}
            </Link>

            <Link
              to="/cart"
              title="Shopping Cart"
              aria-label={`Shopping Cart${cartItemCount ? `, ${cartItemCount} item${cartItemCount === 1 ? '' : 's'}` : ''}`}
              className="group relative flex h-10 w-10 shrink-0 items-center justify-center gap-2 overflow-visible rounded-xl border border-indigo-400/50 bg-gradient-to-br from-indigo-500/25 to-purple-600/15 text-indigo-100 shadow-[0_6px_18px_rgba(79,70,229,0.2)] transition-all hover:border-indigo-300/80 hover:from-indigo-500/35 hover:to-purple-600/25 hover:text-white hover:shadow-[0_8px_28px_rgba(79,70,229,0.38)] sm:h-11 sm:w-11 xl:h-[58px] xl:w-[190px] xl:justify-start xl:gap-3 xl:px-4 min-[1500px]:w-[200px]"
            >
              <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-500 text-white shadow-[0_0_18px_rgba(99,102,241,0.5)] xl:h-10 xl:w-10">
                {renderIcon(FiShoppingCart, { size: 23, strokeWidth: 2.3 })}
                {cartItemCount > 0 && (
                  <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-[#111111] bg-purple-500 px-1 text-[10px] font-black leading-none text-white shadow-lg shadow-purple-900/50 transition-transform group-hover:scale-110">
                    {cartItemCount}
                  </span>
                )}
              </span>
              <span className="hidden min-w-0 flex-col items-start leading-tight xl:flex">
                <span className="whitespace-nowrap text-sm font-black text-white min-[1500px]:text-base">
                  {cartTotal.toLocaleString('en-LK')} LKR
                </span>
                <span className="mt-1 whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.1em] text-indigo-200 min-[1500px]:text-[10px]">
                  Shopping Cart
                </span>
              </span>
            </Link>
          </div>
        </div>

        <div className="mt-3 lg:hidden">
          <HeaderSearch />
        </div>
      </div>
    </div>
  );
};

export default TopBar;
